import { useState } from "react";
import { useBranch } from "../contexts/BranchContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";
import galleryManifest from "../data/galleryManifest.json";
import { branchGalleryAssetPath } from "../utils/branchGallery";

const branchLabels = { caddebostan: "Caddebostan", zeytinburnu: "Zeytinburnu" };

const branchCaptions = { caddebostan: "Tepsi Et Caddebostan", zeytinburnu: "Tepsi Et Zeytinburnu" };

/** Manifest: public/assets/caddebostan ve zeytinburnu (yarn generate-gallery). */
const localGallery = Object.fromEntries(
  Object.entries(galleryManifest).map(([branch, filenames]) => [
    branch,
    filenames.map((filename) => ({
      id: `${branch}-${filename}`,
      image_url: branchGalleryAssetPath(branch, filename),
      caption: branchCaptions[branch] || branchLabels[branch],
      category: "Mekân",
    })),
  ])
);

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

export default function GalleryPage() {
  const { selectedBranch } = useBranch();
  const { theme } = useTheme();
  const { t } = useLang();
  const dk = theme === "dark";
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const items = localGallery[selectedBranch] || [];
  const textH = dk ? "text-white" : "text-[#1A1A1A]";

  const openLightbox = (index) => { setCurrentIndex(index); setLightboxOpen(true); };
  const goNext = () => setCurrentIndex((p) => (p + 1) % items.length);
  const goPrev = () => setCurrentIndex((p) => (p - 1 + items.length) % items.length);

  return (
    <div data-testid="gallery-page" className="min-h-screen">
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <div className="absolute inset-0 bg-[#121212]">
          {items[0]?.image_url ? (
            <img src={items[0].image_url} alt={t.galleryPage.label} className="w-full h-full object-cover opacity-40" />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        </div>
        <div className="relative z-10 h-full flex flex-col justify-end pb-12 md:pb-16 max-w-[1200px] mx-auto px-6">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-body text-xs uppercase tracking-[0.3em] text-[#8B1A1A] mb-3">{branchLabels[selectedBranch]} / {t.galleryPage.label}</motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white">
            {t.galleryPage.title1}<span className="italic text-[#8B1A1A]">{t.galleryPage.title2}</span>
          </motion.h1>
        </div>
      </section>

      <section className="py-16 md:py-24 max-w-[1400px] mx-auto px-6" data-testid="gallery-content">
        {items.length === 0 ? (
          <div className={`text-center max-w-md mx-auto font-body ${textH}`}>
            <p className="font-heading text-xl mb-2">{t.galleryPage.noPhotos}</p>
            <p className="text-sm opacity-70">{branchLabels[selectedBranch]} — {t.galleryPage.noPhotosDesc}</p>
          </div>
        ) : null}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item, i) => (
            <motion.div key={item.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} data-testid={`gallery-item-${i}`} className="group relative overflow-hidden cursor-pointer aspect-[4/3]" onClick={() => openLightbox(i)}>
              <img src={item.image_url} alt={item.caption} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-500 flex items-end">
                <div className="p-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <p className="font-heading text-sm text-white">{item.caption}</p>
                  <p className="font-body text-xs text-[#8B1A1A] uppercase tracking-wider mt-1">{item.category}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] md:max-w-[85vw] bg-black/95 border-[#8B1A1A]/20 p-0 overflow-hidden">
          <DialogTitle className="sr-only">{items[currentIndex]?.caption || t.galleryPage.label}</DialogTitle>
          <DialogDescription className="sr-only">{t.galleryPage.label}</DialogDescription>
          {items[currentIndex] && (
            <div className="relative flex items-center justify-center min-h-[60vh]">
              <img src={items[currentIndex].image_url} alt={items[currentIndex].caption} className="max-w-full max-h-[85vh] object-contain" data-testid="lightbox-image" />
              <button onClick={(e) => { e.stopPropagation(); goPrev(); }} data-testid="lightbox-prev" className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center border border-white/20 text-white hover:border-[#8B1A1A] hover:text-[#8B1A1A] transition-colors bg-black/50"><ChevronLeft className="w-6 h-6" /></button>
              <button onClick={(e) => { e.stopPropagation(); goNext(); }} data-testid="lightbox-next" className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center border border-white/20 text-white hover:border-[#8B1A1A] hover:text-[#8B1A1A] transition-colors bg-black/50"><ChevronRight className="w-6 h-6" /></button>
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <p className="font-heading text-white text-lg">{items[currentIndex].caption}</p>
                <p className="font-body text-[#8B1A1A] text-xs uppercase tracking-wider mt-1">{items[currentIndex].category} &bull; {branchLabels[selectedBranch]}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
